import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatDate } from "../lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { InvitesPanel } from "../components/admin/InvitesPanel";
import { CastingRequestsPanel } from "../components/admin/CastingRequestsPanel";

const Stat = ({ label, value, testId }) => (
  <div className="p-6 border-r last:border-r-0 border-white/10">
    <p className="label-xs">{label}</p>
    <p className="font-display text-4xl mt-2" data-testid={testId}>{value}</p>
  </div>
);

export default function Admin() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");

  const load = () => api.get("/admin/members").then((r) => setData(r.data));
  useEffect(() => {
    load();
  }, []);

  const toggleHidden = async (m) => {
    try {
      await api.patch(`/admin/profiles/${m.profile_id}`, { hidden: !m.hidden });
      toast.success(m.hidden ? "Profile visible again" : "Profile hidden");
      load();
    } catch (e) {
      toast.error("Could not update profile");
    }
  };

  if (!data) return <div className="min-h-[60vh] grid place-items-center label-xs">Loading…</div>;
  const f = filter.toLowerCase();
  const rows = data.members.filter((m) => !f || [m.name, m.email, m.town, m.region].join(" ").toLowerCase().includes(f));

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-16" data-testid="admin-page">
      <p className="label-xs">Admin</p>
      <h1 className="font-display text-4xl sm:text-5xl font-bold mt-3">Members</h1>
      <div className="grid grid-cols-3 border border-white/10 mt-10">
        <Stat label="Members" value={data.stats.total} testId="admin-stat-total" />
        <Stat label="Live profiles" value={data.stats.active} testId="admin-stat-active" />
        <Stat label="Payments" value={data.stats.payments} testId="admin-stat-payments" />
      </div>
      <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter by name, email or area" className="mt-10 max-w-sm" data-testid="admin-filter-input" />
      <div className="border border-white/10 mt-6 overflow-x-auto">
        <Table data-testid="admin-members-table">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Area</TableHead><TableHead>Photos</TableHead>
              <TableHead>Status</TableHead><TableHead>Subscription</TableHead><TableHead>Live until</TableHead><TableHead>Joined</TableHead><TableHead>Agreed</TableHead><TableHead>Hidden</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((m) => (
              <TableRow key={m.profile_id} data-testid={`admin-row-${m.profile_id}`}>
                <TableCell>{m.active ? <Link to={`/${m.slug}`} className="hover:text-primary">{m.name}</Link> : m.name}</TableCell>
                <TableCell className="text-white/70">{m.email}</TableCell>
                <TableCell className="text-white/70">{[m.town, m.region].filter(Boolean).join(", ") || "—"}</TableCell>
                <TableCell>{m.photos}</TableCell>
                <TableCell><span className={m.active ? "text-primary" : "text-white/50"}>{m.active ? "Live" : m.hidden ? "Hidden" : "Not live"}</span></TableCell>
                <TableCell className="capitalize text-white/70" data-testid={`admin-sub-${m.profile_id}`}>{m.subscription}</TableCell>
                <TableCell>{formatDate(m.paid_until) || "—"}</TableCell>
                <TableCell>{formatDate(m.joined)}</TableCell>
                <TableCell className="text-white/70" data-testid={`admin-agreed-${m.profile_id}`}>{formatDate(m.agreed_at) || "—"}</TableCell>
                <TableCell><Switch checked={m.hidden} onCheckedChange={() => toggleHidden(m)} data-testid={`admin-hide-toggle-${m.profile_id}`} /></TableCell>
              </TableRow>
            ))}
            {!rows.length && (
              <TableRow><TableCell colSpan={10} className="text-center text-white/50 py-10" data-testid="admin-empty">No members yet</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <CastingRequestsPanel />
      <InvitesPanel />
    </div>
  );
}
